// src/pages/Transactions/TransactionsController.ts
import {useState, useEffect} from 'react'
import {getTransactions} from '../../services/Transactions'
import {Transaction} from '../../types'
import {FilterType, StatusFilter, DateRange, SortOrder, Totals} from './TransactionsModel'

export const useTransactionsController = () => {
    const [transactions, setTransactions] = useState<Transaction[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState<FilterType>('all')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
    const [dateRange, setDateRange] = useState<DateRange>('all')
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const itemsPerPage = 50

    useEffect(() => {
        fetchTransactions()
    }, [page])

    const fetchTransactions = async (refresh = false) => {
        if (refresh) {
            setRefreshing(true)
            setPage(1)
        } else {
            setLoading(true)
        }

        try {
            const data = await getTransactions(itemsPerPage, refresh ? 0 : (page - 1) * itemsPerPage)
            if (refresh || page === 1) {
                setTransactions(data)
            } else {
                setTransactions(prev => [...prev, ...data])
            }
            setHasMore(data.length === itemsPerPage)
        } catch (error) {
            console.error('Error fetching transactions:', error)
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    const handleRefresh = () => {
        fetchTransactions(true)
    }

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            setPage(prev => prev + 1)
        }
    }

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen)
    }

    const getDateFilter = () => {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

        switch (dateRange) {
            case 'today':
                return (date: Date) => date >= today
            case 'week':
                const weekAgo = new Date(today)
                weekAgo.setDate(weekAgo.getDate() - 7)
                return (date: Date) => date >= weekAgo
            case 'month':
                const monthAgo = new Date(today)
                monthAgo.setMonth(monthAgo.getMonth() - 1)
                return (date: Date) => date >= monthAgo
            default:
                return () => true
        }
    }

    const filteredTransactions = transactions
        .filter(tx => {
            const matchesType = filter === 'all' || tx.type === filter
            const matchesStatus = statusFilter === 'all' || tx.status === statusFilter
            const matchesSearch =
                tx.user_id.includes(search) ||
                tx.id.toString().includes(search) ||
                (tx.phone && tx.phone.includes(search))

            const dateFilter = getDateFilter()
            const matchesDate = dateFilter(new Date(tx.created_at))

            return matchesType && matchesStatus && matchesSearch && matchesDate
        })
        .sort((a, b) => {
            const dateA = new Date(a.created_at).getTime()
            const dateB = new Date(b.created_at).getTime()
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
        })

    const totals: Totals = filteredTransactions.reduce(
        (acc, tx) => {
            acc.usd += tx.amount_usd
            acc.lbp += tx.amount_lbp
            acc.fees += tx.fee_usd
            acc.count++
            return acc
        },
        {usd: 0, lbp: 0, fees: 0, count: 0}
    )

    const exportToCSV = () => {
        const csv = [
            ['ID', 'المستخدم', 'النوع', 'المبلغ USD', 'المبلغ LBP', 'الرسوم', 'الحالة', 'التاريخ'].join(','),
            ...filteredTransactions.map(tx => [
                tx.id,
                tx.user_id,
                tx.type,
                tx.amount_usd,
                tx.amount_lbp,
                tx.fee_usd,
                tx.status,
                tx.created_at
            ].join(','))
        ].join('\n')

        const blob = new Blob([csv], {type: 'text/csv'})
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `transactions_${new Date().toISOString()}.csv`
        a.click()
    }

    return {
        // State
        transactions,
        filteredTransactions,
        loading,
        refreshing,
        search,
        filter,
        statusFilter,
        sortOrder,
        dateRange,
        page,
        hasMore,
        sidebarOpen,
        totals,

        // Setters
        setSearch,
        setFilter,
        setStatusFilter,
        setSortOrder,
        setDateRange,

        // Actions
        handleRefresh,
        handleLoadMore,
        toggleSidebar,
        exportToCSV
    }
}